import {
  ComponentPropsWithoutRef,
  forwardRef,
  MouseEvent,
  KeyboardEvent,
  useLayoutEffect,
  useRef,
  FocusEvent,
  useContext,
} from "react";
import cx from "classnames";

import { getKeyboardFocusableElements } from "../../internal/functions";
import { ThemeProvider } from "../../providers";
import { ThemeProviderContext } from "../../providers/ThemeProvider/internal/ThemeProviderContext";
import { useMergeRefs } from "../../hooks";

import { useTooltipContext } from "./internal/useTooltipContext";

import styles from "./Tooltip.module.scss";

/**
 * Props for the TooltipContent component
 * @extends Omit<ComponentPropsWithoutRef<"div">, "popover">
 */
export type TooltipContentProps = Omit<
  ComponentPropsWithoutRef<"div">,
  "popover"
>;

const TooltipContentElement = forwardRef<HTMLDivElement, TooltipContentProps>(
  function TooltipContent(props, ref) {
    const {
      children,
      className,
      style,
      onMouseEnter,
      onMouseLeave,
      onKeyDown,
      onFocus,
      onBlur,
      id,
      ...rest
    } = props;
    const context = useTooltipContext();
    const { mode: currentMode } = useContext(ThemeProviderContext);
    const tooltipClassNames = cx(className, styles["content"]);
    const childrenRef = useRef<HTMLDivElement>(null);
    const focusItems = useRef<Element[]>();
    const combinedStyles = {
      ...style,
      ...context?.tooltip.style,
    };

    // Determine the opposite mode for the tooltip
    const tooltipMode = currentMode === "dark" ? "light" : "dark";

    // TODO: getFocusableElement is not reliable
    // Need update!!!!! https://servicetitan.atlassian.net/browse/ANV-2605
    useLayoutEffect(() => {
      if (!childrenRef.current) return;
      const focusElements = getKeyboardFocusableElements(childrenRef.current);
      focusItems.current = focusElements;
    }, [childrenRef, children, context?.open]);

    useLayoutEffect(() => {
      if (id) {
        context?.tooltip.setId(id);
      }
    }, [id, context?.tooltip.setId, context?.tooltip]);

    const onMouseEnterHandler = (e: MouseEvent<HTMLDivElement>) => {
      onMouseEnter?.(e);
      if (context?.controlled) return;
      context?.tooltip.show();
    };
    const onMouseLeaveHandler = (e: MouseEvent<HTMLDivElement>) => {
      onMouseLeave?.(e);
      if (context?.controlled) return;
      context?.tooltip.close();
    };
    const onFocusHandler = (e: FocusEvent<HTMLDivElement>) => {
      onFocus?.(e);
      if (context?.controlled) return;
      context?.tooltip.show();
    };
    const onBlurHandler = (e: FocusEvent<HTMLDivElement>) => {
      onBlur?.(e);
      if (context?.controlled) return;
      context?.tooltip.close();
    };

    const onKeyDownHandler = (e: KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(e);
      if (!context?.open) return;
      if (e.code === "Escape") {
        context?.tooltip.close();
      }
    };

    const hasSimpleContent = typeof children === "string";
    const role = hasSimpleContent ? "tooltip" : "region";
    const tooltipWrapperRef = useMergeRefs([context?.tooltip.ref, ref]);

    return (
      <div
        popover="manual"
        data-anv="tooltip-content"
        className={tooltipClassNames}
        ref={tooltipWrapperRef}
        style={combinedStyles}
        onMouseEnter={onMouseEnterHandler}
        onMouseLeave={onMouseLeaveHandler}
        onBlur={onBlurHandler}
        onFocus={onFocusHandler}
        role={role}
        id={context?.tooltip.id}
        {...rest}
      >
        <div
          className={styles.scroller}
          role="presentation"
          tabIndex={-1}
          ref={childrenRef}
          key={context?.tooltip.key}
          onKeyDown={onKeyDownHandler}
        >
          {hasSimpleContent ? (
            children
          ) : (
            <ThemeProvider mode={tooltipMode} data-anv="tooltip-content-theme">
              {children}
            </ThemeProvider>
          )}
        </div>
        <span className={styles["arrow"]} ref={context?.tooltip.arrowRef}>
          <svg aria-hidden="true" width="14" height="14" viewBox="0 0 14 14">
            <path stroke="none" d="M0,0 H14 L7,7 Q7,7 7,7 Z"></path>
            <clipPath id=":r9:">
              <rect x="0" y="0" width="14" height="14"></rect>
            </clipPath>
          </svg>
        </span>
      </div>
    );
  },
);

/**
 * TooltipContent component that displays the tooltip content.
 *
 * Features:
 * - Displays the tooltip content with proper positioning
 * - Includes an arrow pointing to the trigger element
 * - Supports keyboard navigation and escape key to close
 * - Handles mouse interactions for hover behavior
 * - Accessible with proper ARIA roles
 * - Supports all standard div element props
 * - Automatic focus management for keyboard navigation
 * - Real-time position updates
 * - Handles both string and complex content
 *
 * @example
 * <Tooltip.Content>
 *   This tooltip provides helpful context
 * </Tooltip.Content>
 */
export const TooltipContent = forwardRef<HTMLDivElement, TooltipContentProps>(
  function TooltipContent(props, ref) {
    return <TooltipContentElement {...props} ref={ref} />;
  },
);
